import {
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
} from "../../../../graphql/types";
import type {
  CmpInvoiceFieldsFragment,
  CmpMemberMembershipFieldsFragment,
} from "../../../../graphql/types";

type SavedMembership = Pick<CmpMemberMembershipFieldsFragment,
  "membershipTypeName" | "pricingSnapshot" | "selectedAdditionalCostIds">;
type SavedInvoice = Pick<CmpInvoiceFieldsFragment,
  "id" | "chamberMemberId" | "invoiceKind" | "invoiceStatus" | "lineItems" | "pricingSnapshot" | "totalAmountInCents">;

/** Read-only values come from saved accounting evidence, never current settings. */
export const getMemberBillingSnapshot = ({ memberId, membership, invoices }: {
  memberId: string;
  membership: SavedMembership | null | undefined;
  invoices: readonly SavedInvoice[];
}) => {
  if (!membership) return null;
  const selectedIds = Array.from(new Set(membership.selectedAdditionalCostIds || []));
  const addOnLineIds = new Set(selectedIds.map((id) => `membership-add-on-${id}`));
  const candidates = invoices.filter((invoice) => {
    if (invoice.chamberMemberId !== memberId || invoice.invoiceKind !== CmpInvoiceKindEnum.AddOn ||
      invoice.invoiceStatus === CmpInvoiceStatusEnum.Void || invoice.invoiceStatus === CmpInvoiceStatusEnum.Draft) return false;
    const lines = (invoice.lineItems || []).filter((line) => line.id.startsWith("membership-add-on-"));
    return selectedIds.length > 0 && lines.length === selectedIds.length &&
      lines.every((line) => addOnLineIds.has(line.id));
  });
  // Duplicate or missing history is not proof of which add-on invoice was charged.
  const addOnInvoice = candidates.length === 1 ? candidates[0] : undefined;
  const addOnAmountsInCents = new Map<string, number>();
  for (const id of selectedIds) {
    const lineId = `membership-add-on-${id}`;
    const line = addOnInvoice?.lineItems?.find((item) => item.id === lineId) ||
      membership.pricingSnapshot.lineItems?.find((item) => item.id === lineId);
    if (line) addOnAmountsInCents.set(id, line.subtotalAmountInCents);
  }
  const knownAddOnAmounts = selectedIds.every((id) => addOnAmountsInCents.has(id));
  const snapshot = membership.pricingSnapshot;
  return {
    membershipTypeName: membership.membershipTypeName,
    pricingKey: snapshot.pricingKey,
    currency: snapshot.currency,
    baseAmountInCents: snapshot.baseAmountInCents,
    membershipInvoiceTotalInCents: snapshot.totalAmountInCents,
    addOnAmountsInCents,
    addOnSubtotalInCents: knownAddOnAmounts
      ? selectedIds.reduce((total, id) => total + (addOnAmountsInCents.get(id) || 0), 0)
      : null,
    addOnInvoiceTotalInCents: selectedIds.length === 0 ? 0 : addOnInvoice?.totalAmountInCents ?? null,
  };
};
